import type { DiagnosticProcedureDto } from '@/api/WhisApi/generated';
import { FoldableSection } from '@/components/FoldableSection/FoldableSection';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { getStep3DetailFormConfig } from '@/screens/ReportCreation/forms/step3.config';
import type { FormSection } from '@/screens/ReportCreation/reportCreation.types';
import { useMemo } from 'react';
import { SizableText, YStack } from 'tamagui';
import {
  getDiagnosticProcedureFieldValue,
  hasMissingRequiredFields,
} from '../reportDetail.utils';
import { ReportDetailFieldRenderer } from './ReportDetailFieldRenderer/ReportDetailFieldRenderer';
import { ReportDetailSection } from './ReportDetailSection';

interface ReportDetailDiagnosticProceduresProps {
  procedures: DiagnosticProcedureDto[];
  isDraftStatus: boolean;
  defaultOpen?: boolean;
}

export const ReportDetailDiagnosticProcedures = ({
  isDraftStatus,
  procedures,
  defaultOpen = false,
}: ReportDetailDiagnosticProceduresProps): React.ReactElement | null => {
  const { t, language } = useAppTranslation();

  const sections = useMemo<FormSection[]>(
    () =>
      getStep3DetailFormConfig(t, language).repeatableSections?.[0]?.sections ??
      [],
    [t, language],
  );

  const diagnosticMissing = useMemo(() => {
    if (!procedures.length) {
      return false;
    }

    return procedures.some((procedure) =>
      hasMissingRequiredFields(sections, (fieldId) =>
        getDiagnosticProcedureFieldValue(procedure, fieldId, language),
      ),
    );
  }, [procedures, sections, language]);

  if (!procedures.length) {
    return null;
  }

  return (
    <ReportDetailSection>
      <FoldableSection
        title={t('reportDetail.sections.diagnosticProcedure')}
        defaultOpen={defaultOpen}
        showSectionIndicator={isDraftStatus && diagnosticMissing}
        sectionIndicatorLabel={t('reportDetail.missingIndicator')}
      >
        <YStack gap="$4">
          {procedures.map((procedure, index) => (
            <YStack key={procedure.id} gap="$3">
              <SizableText size="$body" fontFamily="$bold">
                {t('reportDetail.diagnosticProcedureLabel', {
                  index: index + 1,
                })}
              </SizableText>
              <ReportDetailFieldRenderer
                sections={sections}
                source={{ kind: 'diagnosticProcedure', procedure }}
              />
            </YStack>
          ))}
        </YStack>
      </FoldableSection>
    </ReportDetailSection>
  );
};
